// safety/incidents-and-injuries/components/tabs/ExpensesTab.tsx
import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { SectionTitle, AddButton, FormModal } from '../shared';
import { fmtDate, money, todayISO } from '../../utils/incidents-utils';
import type { Incident, Expense } from '../../../../../types/incident.types';

const YesNoPill: React.FC<{ v: boolean }> = ({ v }) =>
    v ? (
        <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">Yes</span>
    ) : (
        <span className="text-xs font-medium text-destructive bg-destructive/5 px-2 py-0.5 rounded-md">No</span>
    );

export const ExpensesTab: React.FC<{
    incident: Incident;
    onAdd: (e: Expense) => void;
    onDelete: (id: string) => void;
}> = ({ incident: r, onAdd, onDelete }) => {
    const [modal, setModal] = useState(false);
    const total = r.expenses.reduce((a, e) => a + +e.amount, 0);
    const reimb = r.expenses.filter((e) => e.reimbursed).reduce((a, e) => a + +e.amount, 0);
    const out = total - reimb;

    return (
        <>
            <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="rounded-lg border border-border p-4">
                    <div className="text-xs text-muted-foreground">Total Medical Costs</div>
                    <div className="text-2xl font-semibold mt-1">{money(total)}</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                    <div className="text-xs text-muted-foreground">Reimbursed</div>
                    <div className="text-2xl font-semibold mt-1 text-emerald-600">{money(reimb)}</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4">
                    <div className="text-xs text-muted-foreground">Outstanding</div>
                    <div className="text-2xl font-semibold mt-1 text-amber-600">{money(out)}</div>
                </div>
            </div>

            <SectionTitle title="Expense Records" right={<AddButton label="Add Expense" onClick={() => setModal(true)} />} />
            <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-muted/30 border-b border-border">
                        <tr>
                            {['Type', 'Provider', 'Invoice', 'Date', 'Amount', 'Paid', 'Reimbursed', ''].map((h) => (
                                <th key={h} className="text-left font-medium text-[11px] uppercase tracking-wide text-muted-foreground px-3 py-2.5">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {r.expenses.length === 0 && (
                            <tr>
                                <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                    No expenses yet.
                                </td>
                            </tr>
                        )}
                        {r.expenses.map((e) => (
                            <tr key={e.id} className="border-b border-border last:border-0">
                                <td className="px-3 py-3 font-medium">{e.type}</td>
                                <td className="px-3 py-3 text-muted-foreground">{e.provider}</td>
                                <td className="px-3 py-3 text-primary">{e.invoice}</td>
                                <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(e.date)}</td>
                                <td className="px-3 py-3 font-medium">{money(e.amount)}</td>
                                <td className="px-3 py-3"><YesNoPill v={e.paid} /></td>
                                <td className="px-3 py-3"><YesNoPill v={e.reimbursed} /></td>
                                <td className="px-3 py-3 text-right">
                                    <button onClick={() => onDelete(e.id)} className="text-muted-foreground hover:text-destructive">
                                        <Trash2 size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modal && (
                <FormModal
                    title="Add Expense"
                    fields={[
                        { key: 'type', label: 'Type', value: '' },
                        { key: 'provider', label: 'Provider', value: '' },
                        { key: 'invoice', label: 'Invoice #', value: `INV-${new Date().getFullYear()}-${Math.floor(Math.random() * 900 + 100)}` },
                        { key: 'date', label: 'Date', type: 'date', value: todayISO() },
                        { key: 'amount', label: 'Amount ($)', type: 'number', value: '0' },
                        { key: 'paid', label: 'Paid', type: 'yn', value: false },
                        { key: 'reimbursed', label: 'Reimbursed', type: 'yn', value: false },
                    ]}
                    onClose={() => setModal(false)}
                    onSubmit={(v) => {
                        onAdd({ ...(v as any), amount: +v.amount });
                        setModal(false);
                    }}
                />
            )}
        </>
    );
};
